import { join } from "node:path";
import { cp, mkdtemp, rm } from "node:fs/promises";
import { tmpdir } from "node:os";
import { exists, readFile } from "./fsx.js";
import { run, which } from "./exec.js";
import type { ClerkCliDeps } from "./clerk.js";

/**
 * Real wiring for the Clerk CLI tier.
 *
 * The snapshot is the important part. `clerk init` edits application source —
 * verified against this template, it wraps `layout.tsx` in a second
 * `<ClerkProvider>` on top of the one `Providers.tsx` already mounts. Two
 * nested providers is not a harmless duplicate: the outer one carries no
 * appearance config, so brand theming silently changes.
 *
 * So: copy the directories the CLI is known to touch, run it, then put them
 * back. `.env.local` is what we actually want and lives outside the snapshot,
 * so it survives.
 */

/** Directories the CLI may rewrite. Cheap to copy; excludes node_modules. */
const SNAPSHOT_PATHS = ["src", "package.json", "next.config.mjs", "middleware.ts"];

export function clerkCliDeps(): ClerkCliDeps {
  return {
    which,
    run: async (cmd, args, opts) => {
      const r = await run(cmd, args, opts);
      return { ok: r.ok, stdout: r.stdout, stderr: r.stderr };
    },
    readFileIfExists: async (path) => {
      if (!(await exists(path))) return undefined;
      try {
        return await readFile(path, "utf8");
      } catch {
        return undefined;
      }
    },
    snapshot: async (storefront: string) => {
      const backup = await mkdtemp(join(tmpdir(), "carrier-clerk-snap-"));
      const saved: string[] = [];
      for (const rel of SNAPSHOT_PATHS) {
        const src = join(storefront, rel);
        if (!(await exists(src))) continue;
        await cp(src, join(backup, rel), { recursive: true });
        saved.push(rel);
      }
      return async () => {
        try {
          for (const rel of saved) {
            const target = join(storefront, rel);
            await rm(target, { recursive: true, force: true });
            await cp(join(backup, rel), target, { recursive: true });
          }
        } finally {
          await rm(backup, { recursive: true, force: true });
        }
      };
    },
  };
}
